<template>
  <div
    v-if="isDev"
    class="fixed bottom-4 right-4 z-[9999] bg-gray-900 text-white p-4 rounded-lg shadow-lg max-w-md text-xs font-mono"
    style="max-height: 400px; overflow-y: auto"
  >
    <div class="flex items-center justify-between mb-2">
      <h3 class="font-bold text-sm">Auth Debug Info</h3>
      <button class="text-gray-400 hover:text-white" @click="isExpanded = !isExpanded">
        {{ isExpanded ? '−' : '+' }}
      </button>
    </div>

    <div v-if="isExpanded" class="space-y-2">
      <!-- Auth Store State -->
      <div class="border-b border-gray-700 pb-2">
        <div class="font-semibold text-yellow-400">Auth Store:</div>
        <div>
          Initialized:
          <span :class="authStore.isInitialized ? 'text-green-400' : 'text-red-400'">{{
            authStore.isInitialized
          }}</span>
        </div>
        <div>
          Authenticated:
          <span :class="authStore.isAuthenticated ? 'text-green-400' : 'text-red-400'">{{
            authStore.isAuthenticated
          }}</span>
        </div>
        <div>
          Loading:
          <span :class="authStore.loading ? 'text-yellow-400' : 'text-gray-400'">{{
            authStore.loading
          }}</span>
        </div>
        <div>
          Error:
          <span :class="authStore.error ? 'text-red-400' : 'text-gray-400'">{{
            authStore.error?.message || 'None'
          }}</span>
        </div>
      </div>

      <!-- User Info -->
      <div v-if="authStore.user" class="border-b border-gray-700 pb-2">
        <div class="font-semibold text-blue-400">User:</div>
        <div>ID: {{ authStore.user.telegramId }}</div>
        <div>Username: {{ authStore.user.username || 'N/A' }}</div>
        <div>Name: {{ authStore.user.firstName }} {{ authStore.user.lastName || '' }}</div>
        <div>Session ID: {{ authStore.user.sessionId?.substring(0, 8) }}...</div>
        <div>Expires: {{ formatDate(authStore.user.expiresAt) }}</div>
        <div>Tenant: {{ authStore.user.tenantContext || 'None' }}</div>
      </div>

      <!-- Storage Info -->
      <div class="border-b border-gray-700 pb-2">
        <div class="font-semibold text-purple-400">Storage:</div>
        <div>
          Session Valid:
          <span :class="sessionValid ? 'text-green-400' : 'text-red-400'">{{ sessionValid }}</span>
        </div>
        <div>
          Auth Token:
          <span :class="hasAuthToken ? 'text-green-400' : 'text-gray-400'">{{
            hasAuthToken ? 'Present' : 'None'
          }}</span>
        </div>
        <div v-if="hasAuthToken">Token Preview: {{ authTokenPreview }}</div>
        <div v-if="tokenInfo">Token Expires: {{ formatDate(tokenInfo.expiresAt) }}</div>
        <div v-if="tokenInfo">Token Age: {{ formatTokenAge(tokenInfo.createdAt) }}</div>
        <div v-if="tokenInfo">
          Token Status:
          <span :class="tokenInfo.isExpired ? 'text-red-400' : 'text-green-400'">{{
            tokenInfo.isExpired ? 'Expired' : 'Valid'
          }}</span>
        </div>
        <div>
          Intended Route:
          <span :class="hasIntendedRoute ? 'text-blue-400' : 'text-gray-400'">{{
            intendedRoute?.path || 'None'
          }}</span>
        </div>
      </div>

      <!-- Route Info -->
      <div class="border-b border-gray-700 pb-2">
        <div class="font-semibold text-green-400">Route:</div>
        <div>Current: {{ route.path }}</div>
        <div>Name: {{ route.name }}</div>
        <div>
          Requires Auth:
          <span :class="route.meta?.requiresAuth ? 'text-yellow-400' : 'text-gray-400'">{{
            route.meta?.requiresAuth || false
          }}</span>
        </div>
      </div>

      <!-- Session Timing -->
      <div v-if="authStore.user" class="border-b border-gray-700 pb-2">
        <div class="font-semibold text-orange-400">Session Timing:</div>
        <div>Time to Expiry: {{ formatDuration(authStore.timeToExpiry) }}</div>
        <div>
          Expiring Soon:
          <span :class="authStore.isSessionExpiring ? 'text-red-400' : 'text-green-400'">{{
            authStore.isSessionExpiring
          }}</span>
        </div>
        <div>
          Timeout Warning:
          <span :class="authStore.sessionTimeoutWarning ? 'text-red-400' : 'text-gray-400'">{{
            authStore.sessionTimeoutWarning
          }}</span>
        </div>
      </div>

      <!-- Actions -->
      <div class="pt-2">
        <div class="font-semibold text-cyan-400 mb-1">Actions:</div>
        <div class="flex flex-wrap gap-1">
          <button
            class="px-2 py-1 bg-blue-600 hover:bg-blue-700 rounded text-xs"
            :disabled="!authStore.isAuthenticated"
            @click="refreshSession"
          >
            Refresh
          </button>
          <button
            class="px-2 py-1 bg-red-600 hover:bg-red-700 rounded text-xs"
            @click="clearStorage"
          >
            Clear Storage
          </button>
          <button
            class="px-2 py-1 bg-green-600 hover:bg-green-700 rounded text-xs"
            @click="testTokenAuth"
          >
            Test Token
          </button>
          <button
            class="px-2 py-1 bg-purple-600 hover:bg-purple-700 rounded text-xs"
            @click="copyDebugInfo"
          >
            Copy Info
          </button>
          <button
            v-if="hasAuthToken"
            class="px-2 py-1 bg-orange-600 hover:bg-orange-700 rounded text-xs"
            @click="clearAuthToken"
          >
            Clear Persistent Token
          </button>
          <button
            class="px-2 py-1 bg-cyan-600 hover:bg-cyan-700 rounded text-xs"
            @click="refreshTokenInfo"
          >
            Refresh Info
          </button>
        </div>
      </div>

      <!-- Last Update -->
      <div class="text-gray-500 text-xs pt-2 border-t border-gray-700">
        Last update: {{ lastUpdate }}
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted } from 'vue';
import { useRoute } from 'vue-router';
import { useAuthStore } from '@/stores/auth';
import { storageService } from '@/services/storage';

const route = useRoute();
const authStore = useAuthStore();

// State
const isExpanded = ref(false);
const lastUpdate = ref(new Date().toLocaleTimeString());

// Environment check
const isDev = computed(() => import.meta.env.DEV);

// Storage state
const sessionValid = computed(() => storageService.isSessionValid());
const hasAuthToken = computed(() => storageService.hasAuthToken());
const tokenInfo = computed(() => storageService.getAuthTokenInfo());
const authTokenPreview = computed(() => {
  const token = storageService.getAuthToken();
  return token ? `${token.substring(0, 10)}...` : 'N/A';
});
const intendedRoute = computed(() => storageService.getIntendedRoute());
const hasIntendedRoute = computed(() => !!intendedRoute.value);

// Update timer
let updateInterval: number | null = null;

// Methods
const formatDate = (date: string | Date) => {
  if (!date) return 'N/A';
  return new Date(date).toLocaleString();
};

const formatDuration = (ms: number | null) => {
  if (!ms || ms <= 0) return 'Expired';

  const minutes = Math.floor(ms / 60000);
  const seconds = Math.floor((ms % 60000) / 1000);

  if (minutes > 0) {
    return `${minutes}m ${seconds}s`;
  }
  return `${seconds}s`;
};

const formatTokenAge = (createdAt: string) => {
  const created = new Date(createdAt);
  const now = new Date();
  const ageMs = now.getTime() - created.getTime();

  const days = Math.floor(ageMs / (24 * 60 * 60 * 1000));
  const hours = Math.floor((ageMs % (24 * 60 * 60 * 1000)) / (60 * 60 * 1000));

  if (days > 0) {
    return `${days}d ${hours}h`;
  } else if (hours > 0) {
    return `${hours}h`;
  } else {
    const minutes = Math.floor(ageMs / (60 * 1000));
    return `${minutes}m`;
  }
};

const refreshSession = async () => {
  try {
    const success = await authStore.refreshSession();
    console.log('Debug: Session refresh result:', success);
  } catch (error) {
    console.error('Debug: Session refresh error:', error);
  }
};

const clearStorage = () => {
  if (confirm('Clear all storage data? This will log you out.')) {
    storageService.clearAll();
    window.location.reload();
  }
};

const clearAuthToken = () => {
  if (confirm('Clear persistent auth token? This will require re-authentication.')) {
    storageService.clearAuthToken();
  }
};

const refreshTokenInfo = () => {
  updateTimestamp();
};

const testTokenAuth = () => {
  const token = prompt('Enter test token:');
  if (token) {
    storageService.setAuthToken(token);
    authStore.initialize();
  }
};

const copyDebugInfo = () => {
  const info = {
    timestamp: new Date().toISOString(),
    route: {
      path: route.path,
      name: route.name,
      requiresAuth: route.meta?.requiresAuth,
    },
    auth: {
      initialized: authStore.isInitialized,
      authenticated: authStore.isAuthenticated,
      loading: authStore.loading,
      error: authStore.error?.message,
      sessionExpiring: authStore.isSessionExpiring,
      timeToExpiry: authStore.timeToExpiry,
    },
    user: authStore.user
      ? {
          telegramId: authStore.user.telegramId,
          username: authStore.user.username,
          sessionId: authStore.user.sessionId,
          expiresAt: authStore.user.expiresAt,
          tenantContext: authStore.user.tenantContext,
        }
      : null,
    storage: {
      sessionValid: sessionValid.value,
      hasAuthToken: hasAuthToken.value,
      authTokenPreview: authTokenPreview.value,
      tokenInfo: tokenInfo.value,
      intendedRoute: intendedRoute.value,
    },
  };

  navigator.clipboard
    .writeText(JSON.stringify(info, null, 2))
    .then(() => console.log('Debug info copied to clipboard'))
    .catch(err => console.error('Failed to copy debug info:', err));
};

const updateTimestamp = () => {
  lastUpdate.value = new Date().toLocaleTimeString();
};

// Lifecycle
onMounted(() => {
  // Update timestamp every 5 seconds
  updateInterval = window.setInterval(updateTimestamp, 5000);

  // Add keyboard shortcut (Ctrl/Cmd + D) to toggle debug panel
  const handleKeydown = (e: KeyboardEvent) => {
    if ((e.ctrlKey || e.metaKey) && e.key === 'd' && e.shiftKey) {
      e.preventDefault();
      isExpanded.value = !isExpanded.value;
    }
  };

  document.addEventListener('keydown', handleKeydown);

  // Cleanup function
  onUnmounted(() => {
    if (updateInterval) {
      clearInterval(updateInterval);
    }
    document.removeEventListener('keydown', handleKeydown);
  });
});
</script>

<style scoped>
/* Custom scrollbar for debug panel */
::-webkit-scrollbar {
  width: 4px;
}

::-webkit-scrollbar-track {
  background: rgba(255, 255, 255, 0.1);
}

::-webkit-scrollbar-thumb {
  background: rgba(255, 255, 255, 0.3);
  border-radius: 2px;
}

::-webkit-scrollbar-thumb:hover {
  background: rgba(255, 255, 255, 0.5);
}

/* Ensure debug panel is above everything */
.z-\[9999\] {
  z-index: 9999;
}

/* Button hover effects */
button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

button:disabled:hover {
  background-color: inherit !important;
}
</style>
